import { useCallback, useEffect, useRef } from 'react';
import type { Contact } from '@rsmaids/shared/types/contacts';

type Subscribe = (id: string, onChange: (c: Contact) => void, onError?: (e: unknown) => void) => () => void;

// How long a just-saved edit is held over what Firestore reports. The edit reaches Firestore via Wix and its
// webhook, a few seconds later (longer on a cold start); this keeps a slow or failed sync from pinning it forever.
const PIN_MS = 30000;

// Every contact field a save can change. Anything else on the doc (sync ids, timestamps) never counts as "caught up".
const DATA_KEYS = [
  'name', 'firstName', 'lastName', 'company', 'jobTitle', 'position', 'birthdate',
  'email', 'phone', 'address', 'addressLine1',
  'phones', 'emails', 'addresses', 'customFields', 'labels', 'displayNameSource',
];

// Ids on phones/emails/addresses/custom fields are generated on every load, so they are left out of comparisons.
// An empty value and a missing one count as the same.
const stable = (c: Contact, keys: string[]): string => {
  const rec = c as unknown as Record<string, unknown>;
  const picked: Record<string, unknown> = {};
  for (const k of keys) picked[k] = rec[k];
  return JSON.stringify(picked, (key, v) => {
    if (key === 'id' || key === '_id') return undefined;
    if (v === '' || (Array.isArray(v) && v.length === 0)) return null;
    return v;
  });
};

type Pin = { saved: Contact; before: Contact | null; keys: string[]; until: number };

// Kept per contact OUTSIDE any component, so a save made on one screen (the edit form) holds on the contact page
// that is showing the same contact.
const pins = new Map<string, Pin>();
const lastFreshById = new Map<string, Contact>();

/**
 * Call after a successful save. `previous` is the contact as it was before the save: the fields that differ from
 * `saved` are the ones this save changed (if none differ, every field is watched).
 */
export function pinContactSaved(id: string, saved: Contact, previous: Contact) {
  const changed = DATA_KEYS.filter(k => stable(saved, [k]) !== stable(previous, [k]));
  pins.set(id, {
    saved,
    before: lastFreshById.get(id) ?? null,
    keys: changed.length ? changed : DATA_KEYS,
    until: Date.now() + PIN_MS,
  });
}

/**
 * Live Firestore listener for one contact, shared by the mobile and desktop contact pages.
 *
 * A save goes to Wix, and Firestore only gets it when the webhook fires, a few seconds later. Until then every
 * Firestore write (Zoho/Helcim write-backs included) reports the OLD data, which made a just-saved edit snap back.
 * After any successful save, pinSaved()/pinContactSaved() keeps the saved values on screen while snapshots still
 * show the data Firestore held BEFORE the save. The first snapshot that changes the saved fields means the change
 * landed, and Firestore takes over silently. Works for every field, with no per-field handling.
 */
export function useContactLiveSync(
  id: string | undefined,
  contacts: { subscribeToContact: Subscribe },
  onFresh: (fresh: Contact) => void,
  shouldSkip?: () => boolean,
) {
  const onFreshRef = useRef(onFresh);
  const skipRef = useRef(shouldSkip);
  useEffect(() => { onFreshRef.current = onFresh; skipRef.current = shouldSkip; });

  useEffect(() => {
    if (!id) return;
    return contacts.subscribeToContact(
      id,
      (fresh) => {
        lastFreshById.set(id, fresh);
        if (skipRef.current?.()) return;
        const pin = pins.get(id);
        if (pin && Date.now() < pin.until) {
          // No snapshot seen before the save: the first one afterwards is the baseline.
          if (!pin.before) pin.before = fresh;
          // Only the fields this save changed decide whether Firestore has caught up — an unrelated write
          // (a sync touching another field) must not release the hold and bring the old value back.
          if (stable(fresh, pin.keys) === stable(pin.before, pin.keys)) {
            const out = { ...fresh } as unknown as Record<string, unknown>;
            const saved = pin.saved as unknown as Record<string, unknown>;
            for (const k of DATA_KEYS) if (k in saved) out[k] = saved[k];
            console.info(`[useContactLiveSync] holding saved ${pin.keys.join(',')} — snapshot still has the old data`);
            onFreshRef.current(out as unknown as Contact);
            return;
          }
          console.info(`[useContactLiveSync] released — snapshot changed ${pin.keys.join(',')}`);
        }
        pins.delete(id);
        onFreshRef.current(fresh);
      },
      (e) => console.warn('[useContactLiveSync] snapshot error', e),
    );
  }, [id, contacts]);

  const pinSaved = useCallback((saved: Contact, previous: Contact) => {
    if (id) pinContactSaved(id, saved, previous);
  }, [id]);

  return { pinSaved };
}
